import React from 'react';
import {
  Card,
  CardBody,
  Typography,
  Checkbox,
  Chip,
} from '@material-tailwind/react';
import {
  AdjustmentsHorizontalIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  QueueListIcon,
  DocumentDuplicateIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

// Define section order and labels
const SECTION_ORDER = [
  'cover',
  'tableOfContents',
  'about',
  'marketTrends',
  'listingPerformance',
  'onMarketComps',
  'recentlySoldComps',
  'summaryPricing',
  'valuationHistory',
  'strategy',
  'recommendations',
  'assumptions',
];

const SECTION_LABELS = {
  cover: 'Cover',
  tableOfContents: 'TOC',
  about: 'About',
  marketTrends: 'Trends',
  onMarketComps: 'Market comps',
  recentlySoldComps: 'Sold comps',
  summaryPricing: 'Summary',
  strategy: 'Services',
  assumptions: 'Thank You',
  listingPerformance: 'Listing Performance',
  valuationHistory: 'Valuation History',
  recommendations: 'Recommendations',
};

// "Tool" items that appear above the report sections
const TOOL_ITEMS = [
  {
    id: 'draftSettings',
    label: 'Settings',
    subtitle: null,
    Icon: AdjustmentsHorizontalIcon,
    iconColor: 'text-blue-500',
    activeClass: 'border-blue-500 bg-blue-50',
  },
  {
    id: 'aiSettings',
    label: 'AI Settings',
    subtitle: 'Prompt & guidelines',
    Icon: SparklesIcon,
    iconColor: 'text-purple-500',
    activeClass: 'border-purple-500 bg-purple-50',
  },
  {
    id: 'aiCosts',
    label: 'AI Costs',
    subtitle: 'Generation cost breakdown',
    Icon: CurrencyDollarIcon,
    iconColor: 'text-purple-500',
    activeClass: 'border-purple-500 bg-purple-50',
  },
  {
    id: 'listings',
    label: 'Listings',
    subtitle: 'Source data & exclusions',
    Icon: QueueListIcon,
    iconColor: 'text-blue-500',
    activeClass: 'border-blue-500 bg-blue-50',
  },
  {
    id: 'reports',
    label: 'Reports',
    subtitle: 'Published versions',
    Icon: DocumentDuplicateIcon,
    iconColor: 'text-green-500',
    activeClass: 'border-green-500 bg-green-50',
  },
];

const isCustomSection = (id) => id.startsWith('custom_');

// Sort sections by defined order, splicing custom sections in after their
// insertAfter anchor (mirrors the backend's orderSectionIds)
function sortSections(sections) {
  const order = [...SECTION_ORDER];
  const customs = sections
    .filter((s) => isCustomSection(s.id))
    .sort((a, b) => {
      const sa = a.content?.sortIndex ?? 0;
      const sb = b.content?.sortIndex ?? 0;
      return sa - sb || a.id.localeCompare(b.id);
    });

  for (const custom of customs) {
    const anchorIdx = custom.content?.insertAfter ? order.indexOf(custom.content.insertAfter) : -1;
    if (anchorIdx === -1) {
      // Default placement: before the closing Thank You section
      order.splice(Math.max(order.length - 1, 0), 0, custom.id);
      continue;
    }
    let insertAt = anchorIdx + 1;
    while (insertAt < order.length && isCustomSection(order[insertAt])) insertAt++;
    order.splice(insertAt, 0, custom.id);
  }

  return [...sections].sort((a, b) => {
    const indexA = order.indexOf(a.id);
    const indexB = order.indexOf(b.id);
    // Put unknown sections at the end
    const orderA = indexA === -1 ? order.length : indexA;
    const orderB = indexB === -1 ? order.length : indexB;
    return orderA - orderB;
  });
}

function SectionList({
  sections,
  selectedSection,
  onSelectSection,
  onToggleSection,
  onAddCustomSection,
  isAddingSection,
  hasDraftChanges,
  hasCompChanges,
  hasGuidelinesChanges,
  hasExclusionChanges,
  pendingExclusionsCount,
  hasSalesCompExclusionChanges,
  isLocked,
  periodDays,
  reportCount,
  isMarketingUpdate,
}) {
  // Per-item change/status chips
  const renderChips = (id) => {
    if (id === 'draftSettings') {
      return (
        <>
          {hasCompChanges && (
            <Chip size="sm" value="Comps" color="green" variant="ghost" />
          )}
          {hasDraftChanges && (
            <Chip size="sm" value="Modified" color="amber" variant="ghost" />
          )}
        </>
      );
    }
    if (id === 'aiSettings' && hasGuidelinesChanges) {
      return <Chip size="sm" value="Modified" color="amber" variant="ghost" />;
    }
    if (id === 'listings' && hasExclusionChanges) {
      return (
        <Chip
          size="sm"
          value={pendingExclusionsCount > 0 ? `${pendingExclusionsCount} pending` : 'Pending'}
          color="amber"
          variant="ghost"
        />
      );
    }
    if (id === 'reports' && reportCount > 0) {
      return <Chip size="sm" value={reportCount} color="green" />;
    }
    if (id === 'recentlySoldComps' && hasSalesCompExclusionChanges) {
      return <Chip size="sm" value="Modified" color="amber" variant="ghost" />;
    }
    return null;
  };

  return (
    <Card className={`h-full flex flex-col ${isLocked ? 'opacity-50 pointer-events-none' : ''}`}>
      <CardBody className="p-4 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography variant="h6" color="blue-gray">
            Report Sections
          </Typography>
          {isLocked && (
            <Chip size="sm" value="Refreshing..." color="blue" variant="ghost" />
          )}
        </div>
        <div className="flex flex-col gap-2 overflow-y-auto flex-1">
          {/* Tool items */}
          {TOOL_ITEMS.map(({ id, label, subtitle, Icon, iconColor, activeClass }) => (
            <div
              key={id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                selectedSection === id ? activeClass : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => onSelectSection(id)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Icon className={`h-5 w-5 flex-shrink-0 ${iconColor}`} />
                <div className="min-w-0">
                  <Typography variant="small" className="font-medium">
                    {label}
                  </Typography>
                  {subtitle && (
                    <Typography variant="small" color="gray" className="text-xs">
                      {subtitle}
                    </Typography>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 justify-end">{renderChips(id)}</div>
            </div>
          ))}

          {/* Divider */}
          <div className="border-t border-gray-200 my-1" />

          {/* Report sections */}
          {sortSections(sections).map((section) => (
            <div
              key={section.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                selectedSection === section.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => onSelectSection(section.id)}
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={section.included}
                  onChange={() => onToggleSection(section.id, !section.included)}
                  containerProps={{ className: '-ml-2' }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Typography variant="small" className="font-medium">
                  {SECTION_LABELS[section.id]
                    || (isCustomSection(section.id) ? (section.content?.title || 'Custom Section') : section.id)}
                </Typography>
              </div>
              <div className="flex flex-wrap gap-1 justify-end">{renderChips(section.id)}</div>
            </div>
          ))}

          {/* Add custom section */}
          {onAddCustomSection && (
            <div
              className={`flex items-center gap-3 p-3 rounded-lg border border-dashed border-gray-300 transition-colors cursor-pointer hover:bg-gray-50 ${
                isAddingSection ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={onAddCustomSection}
            >
              <PlusIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
              <Typography variant="small" className="font-medium text-blue-500">
                {isAddingSection ? 'Adding...' : 'Add Custom Section'}
              </Typography>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default SectionList;
